// MatterGen job planning (editable-plan spec 0.7). No imports; the only side input is
// the REACT_APP_MATTERGEN_MAX_PARALLEL env read below.
//
// A run of N candidates for one variant splits into jobs. Every job has ONE batch
// shape with at most 8 structures per GPU batch (docs/NOTES.md "Candidate count:
// base-8 doubling pattern"; the service rejects batch_size > 8 and num_batches > 128,
// spec 0.2), so a job never over-generates:
//   - full jobs of `chunk` candidates (chunk / 8 batches of 8);
//   - the remainder r becomes floor(r / 8) batches of 8 plus, when r % 8 > 0, a
//     separate job of one batch of r % 8.
// Example: 100 at chunk 64 is jobs of 64 (8x8), 32 (4x8) and 4 (1x4).

export const MATTERGEN_GPU_BATCH = 8;
export const MATTERGEN_MAX_BATCHES_PER_JOB = 128;
export const DEFAULT_MATTERGEN_CHUNK_SIZE = 64;

// At most this many MatterGen jobs are in flight across a whole run, every variant
// included (spec 0.7). Matches Cloud Run max-instances for MatterGen. The one definition
// of this env read: useToolsRunner sizes its per-run limiter with it, and MattergenRunner
// uses it for a caller that passes no limiter.
export const MATTERGEN_MAX_PARALLEL = Number(process.env.REACT_APP_MATTERGEN_MAX_PARALLEL) || 3;

const MAX_JOB_CANDIDATES = MATTERGEN_GPU_BATCH * MATTERGEN_MAX_BATCHES_PER_JOB; // 1024

/** Snap a chunk size onto a multiple of 8 in [8, 1024]. Missing, zero, negative or
 * non-numeric values fall back to the default 64. */
export function normalizeMattergenChunkSize(chunkSize) {
  const n = Math.floor(Number(chunkSize));
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_MATTERGEN_CHUNK_SIZE;
  const snapped = Math.floor(n / MATTERGEN_GPU_BATCH) * MATTERGEN_GPU_BATCH;
  return Math.min(MAX_JOB_CANDIDATES, Math.max(MATTERGEN_GPU_BATCH, snapped));
}

/**
 * Split `total` candidates into MatterGen jobs of at most `chunkSize` candidates.
 *
 * With `gpus` > 1 the batches of 8 are also spread over up to `gpus` jobs, as evenly as
 * multiples of 8 allow, so a variant runs on several GPUs at once (one GPU per job,
 * docs/superpowers/specs/2026-10-15-mattergen-gpu-per-job-design.md). Generation costs
 * the same per crystal whatever the batch (docs/NOTES.md, L4 benchmark), so the split
 * adds no GPU time beyond each job's model load. Example: 32 over 3 GPUs is 16, 8, 8.
 * `gpus` 1 (the default) keeps the plain chunked split.
 * @returns {Array<{ batch_size: number, num_batches: number, candidates: number }>}
 */
export function planMattergenJobs(total, chunkSize, gpus = 1) {
  const n = Math.floor(Number(total));
  if (!Number.isFinite(n) || n <= 0) return [];
  const chunk = normalizeMattergenChunkSize(chunkSize);
  const lanes = Math.floor(Number(gpus));
  if (Number.isFinite(lanes) && lanes > 1) return spreadMattergenJobs(n, chunk, lanes);
  const jobs = [];
  const fullJobs = Math.floor(n / chunk);
  for (let i = 0; i < fullJobs; i += 1) {
    jobs.push({ batch_size: MATTERGEN_GPU_BATCH, num_batches: chunk / MATTERGEN_GPU_BATCH, candidates: chunk });
  }
  const remainder = n - fullJobs * chunk;
  const eights = Math.floor(remainder / MATTERGEN_GPU_BATCH);
  if (eights > 0) {
    jobs.push({ batch_size: MATTERGEN_GPU_BATCH, num_batches: eights, candidates: eights * MATTERGEN_GPU_BATCH });
  }
  const tail = remainder % MATTERGEN_GPU_BATCH;
  if (tail > 0) jobs.push({ batch_size: tail, num_batches: 1, candidates: tail });
  return jobs;
}

// The batches of 8 dealt over max(lanes, chunk-bound) jobs, larger jobs first, plus the
// usual separate tail job of r % 8. No job exceeds the chunk.
function spreadMattergenJobs(n, chunk, lanes) {
  const eights = Math.floor(n / MATTERGEN_GPU_BATCH);
  const jobs = [];
  if (eights > 0) {
    const perChunk = chunk / MATTERGEN_GPU_BATCH;
    const count = Math.min(eights, Math.max(lanes, Math.ceil(eights / perChunk)));
    const base = Math.floor(eights / count);
    const extra = eights % count;
    for (let i = 0; i < count; i += 1) {
      const k = base + (i < extra ? 1 : 0);
      jobs.push({ batch_size: MATTERGEN_GPU_BATCH, num_batches: k, candidates: k * MATTERGEN_GPU_BATCH });
    }
  }
  const tail = n % MATTERGEN_GPU_BATCH;
  if (tail > 0) jobs.push({ batch_size: tail, num_batches: 1, candidates: tail });
  return jobs;
}

/** Chunk-size menu (spec settled decision 17): the base-8 ladder from 8 to 256. */
export const MATTERGEN_CHUNK_LADDER = [8, 16, 32, 64, 128, 256];

/** The ladder filtered to the plan's per-run max. Always offers at least 8; a missing
 * or non-numeric plan max offers the whole ladder. */
export function mattergenChunkOptions(planMax) {
  const max = Number(planMax);
  if (planMax === undefined || !Number.isFinite(max)) return [...MATTERGEN_CHUNK_LADDER];
  const options = MATTERGEN_CHUNK_LADDER.filter((c) => c <= max);
  return options.length > 0 ? options : [MATTERGEN_CHUNK_LADDER[0]];
}

/** Manual-form validation: ladder membership within the plan max. null when valid. */
export function mattergenChunkError(value, planMax) {
  const options = mattergenChunkOptions(planMax);
  return options.includes(Number(value)) ? null : `Chunk size must be one of ${options.join(', ')}`;
}
